import { useCallback } from 'react'
import axios from 'axios'
import { useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { QUERIES, showError } from '../../../../../_metronic/helpers'
import { useModal } from '../../../../components'
import { marcarNotaVersaoLida } from '../../notas-versao/core/_requests'
import { NotificacaoDetalheModal } from '../components/modal/NotificacaoDetalheModal'
import { getNotificacaoById, marcarNotificacaoComoLida } from './_requests'
import { NotificacaoList, NotificacaoView } from './_models'
import { destinoNotificacao } from './destinoNotificacao'

export type OpcoesAbrirNotificacao = {
  /** Notificação sem referência: abre o detalhe (padrão) ou só marca como lida (linha do sino). */
  semReferencia?: 'detalhe' | 'apenasMarcar'
  /** View já buscada (o `?id=`), para o detalhe não buscar de novo. */
  view?: NotificacaoView
}

export type AbrirNotificacao = (
  notificacao: NotificacaoList | NotificacaoView,
  opcoes?: OpcoesAbrirNotificacao
) => Promise<void>

const ehNaoEncontrado = (erro: unknown) => axios.isAxiosError(erro) && erro.response?.status === 404

/**
 * Ponto único de abertura de uma notificação pelo usuário (sino, lista, toast e `?id=`).
 * Com referência a nota de versão: marca a notificação e a nota como lidas e navega para a nota.
 * Marcar só a notificação (bolinha, "marcar todas") não passa por aqui e não toca na nota.
 */
export const useAbrirNotificacao = (
  marcarComoLida: (id: NotificacaoList['id']) => Promise<void>
): AbrirNotificacao => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { showModal } = useModal()

  return useCallback(
    async (notificacao, opcoes = {}) => {
      if (!notificacao.id) return

      const invalidarNotificacoes = () => {
        queryClient.invalidateQueries([QUERIES.NOTIFICACOES_LIST])
        queryClient.invalidateQueries([QUERIES.NOTIFICACOES_NAO_LIDAS_COUNT])
      }

      const destino = destinoNotificacao(notificacao)

      if (destino.tipo === 'notaVersao') {
        if (!notificacao.lida) {
          try {
            await marcarNotificacaoComoLida(notificacao.id)
          } catch (erro) {
            showError(erro)
          }
        }
        try {
          await marcarNotaVersaoLida(destino.idNota)
        } catch (erro) {
          // Nota excluída ou despublicada: a página de destino mostra "Nota não disponível".
          if (!ehNaoEncontrado(erro)) showError(erro)
        }
        invalidarNotificacoes()
        queryClient.invalidateQueries(QUERIES.NOTAS_VERSAO_VIGENTE)
        queryClient.invalidateQueries(QUERIES.NOTAS_VERSAO_HISTORICO)
        navigate(`/apps/novidades/${destino.idNota}`)
        return
      }

      if (opcoes.semReferencia === 'apenasMarcar') {
        await marcarComoLida(notificacao.id)
        return
      }

      const { view } = opcoes
      showModal({
        title: notificacao.titulo,
        content: <NotificacaoDetalheModal />,
        // `getNotificacaoById` também marca como lida.
        fetchers: {
          notificacao: () => (view ? Promise.resolve(view) : getNotificacaoById(notificacao.id)),
        },
        onClose: invalidarNotificacoes,
      })
    },
    [queryClient, navigate, showModal, marcarComoLida]
  )
}
